/**
 * Identificadores opacos de 128 bits, ordenables por tiempo (estilo ULID),
 * generados en el cliente. INV-5: estables, unicos entre dispositivos, sin
 * coordinacion con ningun servidor.
 *
 * Formato: 48 bits de tiempo (ms desde epoch) + 80 bits de aleatoriedad,
 * codificados en Crockford base32 (26 caracteres), legible y ordenable
 * lexicograficamente igual que temporalmente.
 */

const CROCKFORD_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

export interface RandomSource {
  randomBytes(length: number): Uint8Array;
}

function getCrypto(): Crypto {
  const g = globalThis as { crypto?: Crypto };
  if (!g.crypto || typeof g.crypto.getRandomValues !== "function") {
    throw new Error("No hay fuente de aleatoriedad criptografica disponible (globalThis.crypto)");
  }
  return g.crypto;
}

export const defaultRandomSource: RandomSource = {
  randomBytes(length: number): Uint8Array {
    const bytes = new Uint8Array(length);
    getCrypto().getRandomValues(bytes);
    return bytes;
  },
};

function encodeCrockford(bytes: Uint8Array): string {
  // Codifica bit a bit en grupos de 5 (32 simbolos), sin depender de que
  // len(bytes)*8 sea multiplo de 5.
  let bits = "";
  for (const b of bytes) bits += b.toString(2).padStart(8, "0");
  let out = "";
  for (let i = 0; i + 5 <= bits.length; i += 5) {
    out += CROCKFORD_ALPHABET[parseInt(bits.slice(i, i + 5), 2)];
  }
  return out;
}

/**
 * Genera un identificador de 128 bits ordenable por tiempo.
 * @param now marca de tiempo en ms (inyectable para tests deterministas).
 * @param random fuente de aleatoriedad (inyectable para tests).
 */
export function generateId(now: number = Date.now(), random: RandomSource = defaultRandomSource): string {
  if (!Number.isFinite(now) || now < 0) {
    throw new RangeError(`Marca de tiempo invalida: ${now}`);
  }
  const timeBytes = new Uint8Array(6); // 48 bits
  let t = Math.floor(now);
  for (let i = 5; i >= 0; i--) {
    timeBytes[i] = t % 256;
    t = Math.floor(t / 256);
  }
  const randomBytes = random.randomBytes(10); // 80 bits
  const all = new Uint8Array(16);
  all.set(timeBytes, 0);
  all.set(randomBytes, 6);
  return encodeCrockford(all);
}

/** entry_id: identidad global de un asiento, generada en el cliente al confirmar (ADR OQ-1, condicion 1). */
export function generateEntryId(now?: number, random?: RandomSource): string {
  return generateId(now, random);
}

/** device_id: identidad estable del dispositivo/instalacion, generada una sola vez y persistida localmente. */
export function generateDeviceId(now?: number, random?: RandomSource): string {
  return generateId(now, random);
}
